import { useSessionMutation as useMutation } from "@/lib/query/core/use-session-mutation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { getPrivateSessionState, subscribePrivateSession } from "@/lib/api/private-session";
import type { PublicSellerAuthLoginResponse } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient, ApiJsonClientError } from "@/lib/query/core/api-json-client";

import { changePrivateSession } from "@/shared/services/privateSessionChange";

type SellerLoginInput = {
  phone: string;
  password: string;
};

export function useLoginMutation() {
  const queryClient = useQueryClient();
  const session = useSyncExternalStore(subscribePrivateSession, getPrivateSessionState);
  const feedbackKey = ["public-login-feedback", session.generation];
  const { data: loginFailure } = useQuery<{ status?: number; code?: string; message: string } | null>({
    queryKey: feedbackKey, queryFn: () => null, enabled: false,
  });
  const mutation = useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async (input: SellerLoginInput) => {
      const generation = getPrivateSessionState().generation + 1;
      queryClient.removeQueries({ queryKey: ["public-login-feedback"] });
      try {
        return await changePrivateSession(queryClient, () =>
          apiJsonClient<PublicSellerAuthLoginResponse>(endpoints.public.sellerAuth.login, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        }));
      } catch (caught) {
        // The transition remounts the public form; deliver only its owned refusal.
        if (getPrivateSessionState().generation === generation) {
          queryClient.setQueryData(["public-login-feedback", generation], {
            status: caught instanceof ApiJsonClientError ? caught.status : undefined,
            // Kept so a 403 can say WHY (SELLER_NO_STORE vs inactive access).
            code: caught instanceof ApiJsonClientError ? caught.code : undefined,
            message: caught instanceof Error ? caught.message : "",
          });
        }
        throw caught;
      }
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending || session.changing,
    loginFailure,
    error: mutation.error,
    reset: () => {
      mutation.reset();
      queryClient.setQueryData(feedbackKey, null);
    },
  };
}
